/**
 * Deep Research Content Types and Lengths
 * Shared constants used by both frontend and backend for content configuration
 */

// Content Type definition
export interface ContentType {
  value: string;
  label: string;
  description: string;
  defaultWordCount: string;
}

// Content Length definition
export interface ContentLength {
  value: string;
  label: string;
  min: number;
  max: number;
  description: string;
}

// Content Types Array
export const CONTENT_TYPES: readonly ContentType[] = [
  {
    value: 'linkedin-article',
    label: 'LinkedIn Article',
    description: 'Professional thought leadership, conversational/first-person/industry insights',
    defaultWordCount: '1000-1500 words'
  },
  {
    value: 'blog-post',
    label: 'Blog Post',
    description: 'Engaging web content, accessible/SEO-friendly/actionable',
    defaultWordCount: '800-1200 words'
  },
  {
    value: 'technical-article',
    label: 'Technical Article',
    description: 'In-depth technical, detailed/code examples/technical accuracy',
    defaultWordCount: '1500-2500 words'
  },
  {
    value: 'tutorial',
    label: 'Tutorial/How-To',
    description: 'Step-by-step guide, instructional/clear steps/practical examples',
    defaultWordCount: '1000-2000 words'
  },
  {
    value: 'opinion-piece',
    label: 'Opinion/Editorial',
    description: 'Thought-provoking commentary, persuasive/personal perspective/strong voice',
    defaultWordCount: '800-1500 words'
  },
  {
    value: 'case-study',
    label: 'Case Study',
    description: 'Real-world analysis, data-driven/problem-solution/evidence-based',
    defaultWordCount: '1500-2500 words'
  },
  {
    value: 'white-paper',
    label: 'White Paper',
    description: 'Authoritative research, formal tone/data-heavy/comprehensive analysis',
    defaultWordCount: '2500-4000 words'
  },
  {
    value: 'news-article',
    label: 'News Article',
    description: 'Timely reporting, factual/inverted pyramid/objective tone',
    defaultWordCount: '500-800 words'
  },
  {
    value: 'listicle',
    label: 'Listicle',
    description: 'Numbered list format, scannable/actionable tips/engaging headers',
    defaultWordCount: '800-1500 words'
  },
  {
    value: 'interview-qa',
    label: 'Interview/Q&A',
    description: 'Conversational dialogue, expert insights/direct quotes/personal perspectives',
    defaultWordCount: '1000-2000 words'
  },
  {
    value: 'product-review',
    label: 'Product Review',
    description: 'Detailed evaluation, pros-cons/practical testing/recommendations',
    defaultWordCount: '1200-2000 words'
  },
  {
    value: 'research-summary',
    label: 'Research Summary',
    description: 'Academic/scientific digest, literature review/evidence synthesis',
    defaultWordCount: '1500-2500 words'
  }
] as const;

// Content Lengths Array
export const CONTENT_LENGTHS: readonly ContentLength[] = [
  {
    value: 'micro',
    label: 'Micro (500-750 words)',
    min: 500,
    max: 750,
    description: 'Quick read, single focused idea'
  },
  {
    value: 'short',
    label: 'Short (750-1250 words)',
    min: 750,
    max: 1250,
    description: 'Concise article'
  },
  {
    value: 'standard',
    label: 'Standard (1000-1500 words)',
    min: 1000,
    max: 1500,
    description: 'Typical article length'
  },
  {
    value: 'medium',
    label: 'Medium (1500-2000 words)',
    min: 1500,
    max: 2000,
    description: 'Comprehensive coverage'
  },
  {
    value: 'long',
    label: 'Long (2000-3000 words)',
    min: 2000,
    max: 3000,
    description: 'Thorough exploration'
  },
  {
    value: 'extended',
    label: 'Extended (3000-5000 words)',
    min: 3000,
    max: 5000,
    description: 'Comprehensive guide'
  }
] as const;

// Source Age definition
export interface SourceAge {
  value: string;
  label: string;
  description: string;
}

/**
 * Source Age Options Array
 *
 * NOTE: The 'value' fields must match src/constants/sourceAge.ts (backend validation).
 * If adding/removing options, update both files to maintain synchronization.
 */
export const SOURCE_AGE_OPTIONS: readonly SourceAge[] = [
  {
    value: 'day',
    label: 'Last 24 hours',
    description: 'Very recent sources only'
  },
  {
    value: 'week',
    label: 'Last week',
    description: 'Past 7 days'
  },
  {
    value: 'month',
    label: 'Last month',
    description: 'Past 30 days'
  },
  {
    value: '3months',
    label: 'Last 3 months',
    description: 'Past 90 days'
  },
  {
    value: '6months',
    label: 'Last 6 months',
    description: 'Past 180 days'
  },
  {
    value: 'year',
    label: 'Last year',
    description: 'Past 12 months'
  },
  {
    value: 'any',
    label: 'Any time',
    description: 'No date restriction'
  }
] as const;

// Type exports for convenience
export type ContentTypeValue = typeof CONTENT_TYPES[number]['value'];
export type ContentLengthValue = typeof CONTENT_LENGTHS[number]['value'];
export type SourceAgeValue = typeof SOURCE_AGE_OPTIONS[number]['value'];